// Generat automat din README.md
import React, { useState } from 'react';
import { Mail, Send, Users, TrendingUp, Eye, MousePointer, Calendar, Plus, Play, Pause, Edit, Copy, Download, Target, Zap, BarChart3, Clock } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const CampaniiEmail = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCampaignModal, setShowCampaignModal] = useState(false);

  // Mock data pentru email marketing
  const emailData = {
    overview: {
      totalCampaigns: 47,
      activeCampaigns: 8,
      totalSubscribers: 12450,
      monthlyGrowth: '+15.3%'
    },
    performance: {
      avgOpenRate: 28.5,
      avgClickRate: 4.2,
      avgConversionRate: 2.1,
      avgUnsubscribeRate: 0.8
    },
    campaigns: [
      {
        id: 1,
        name: 'Newsletter Martie 2024',
        type: 'Newsletter',
        status: 'Trimis',
        subject: '🚀 Noutățile lunii Martie + Oferte exclusive',
        audience: 'Toți subscriberii',
        audienceSize: 12450,
        sentDate: '2024-03-15 10:00',
        openRate: 32.4,
        clickRate: 5.1,
        conversionRate: 2.8,
        unsubscribes: 15,
        bounces: 23,
        revenue: 8950,
        personalization: 'Înaltă',
        aiOptimization: true,
        template: 'Newsletter Standard'
      },
      {
        id: 2,
        name: 'Campanie Remarketing - Coș Abandonat',
        type: 'Automată',
        status: 'Activă',
        subject: 'Ai uitat ceva în coș! Ofertă specială te așteaptă',
        audience: 'Coș abandonat (3 zile)',
        audienceSize: 234,
        openRate: 45.2,
        clickRate: 12.3,
        conversionRate: 8.7,
        unsubscribes: 2,
        bounces: 1,
        revenue: 5680,
        personalization: 'Înaltă',
        aiOptimization: true,
        template: 'Abandoned Cart'
      },
      {
        id: 3,
        name: 'Promoție Primăvară - Early Bird',
        type: 'Promoțională',
        status: 'Programată',
        subject: 'Early Bird: -30% reduceri de primăvară doar pentru tine!',
        audience: 'Clienți Premium',
        audienceSize: 1850,
        scheduledDate: '2024-03-20 09:00',
        openRate: 0,
        clickRate: 0,
        conversionRate: 0,
        unsubscribes: 0,
        bounces: 0,
        revenue: 0,
        personalization: 'Medie',
        aiOptimization: true,
        template: 'Promotional Campaign'
      }
    ],
    segments: [
      {
        id: 1,
        name: 'Clienți Premium',
        size: 1850,
        avgOpenRate: 38.5,
        avgClickRate: 6.2,
        avgRevenue: 450,
        lastActivity: '2024-03-15',
        growthRate: '+12%'
      },
      {
        id: 2,
        name: 'Noi Subscriberi',
        size: 890,
        avgOpenRate: 45.2,
        avgClickRate: 8.1,
        avgRevenue: 180,
        lastActivity: '2024-03-15',
        growthRate: '+34%'
      }
    ],
    templates: [
      {
        id: 1,
        name: 'Newsletter Standard',
        category: 'Newsletter',
        description: 'Layout modern pentru newsletter-e săptămânale cu secțiuni personalizabile',
        usage: 78,
        conversionRate: 3.2,
        lastUpdated: '2024-03-10',
        aiOptimized: true
      },
      {
        id: 2,
        name: 'Promotional Campaign',
        category: 'Promoțional',
        description: 'Template pentru campanii promoționale cu focus pe call-to-action',
        usage: 45,
        conversionRate: 5.8,
        lastUpdated: '2024-03-05',
        aiOptimized: true
      }
    ],
    aiInsights: [
      {
        id: 1,
        insight: 'Emailurile trimise Marțea între 10-11 AM au open rate cu 23% mai mare',
        confidence: 92,
        impact: 'Înalt',
        recommendation: 'Reprogramează campaniile majore pentru această proiect optimă',
        category: 'Timing Optimization'
      }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Trimis': return 'success';
      case 'Activă': return 'info';
      case 'Programată': return 'warning';
      case 'Draft': return 'default';
      default: return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Newsletter': return 'info';
      case 'Promoțională': return 'success';
      case 'Automată': return 'warning';
      default: return 'default';
    }
  };

  const campaignColumns = [
    { 
      header: 'Campanie', 
      key: 'name',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500 max-w-xs truncate">{row.subject}</div>
        </div>
      )
    },
    { 
      header: 'Tip', 
      key: 'type',
      render: (value) => <Badge variant={getTypeColor(value)}>{value}</Badge>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Audience', 
      key: 'audience',
      render: (value, row) => (
        <div className="text-sm">
          <div className="font-medium">{row.audienceSize.toLocaleString('ro-RO')}</div>
          <div className="text-gray-500 text-xs">{value}</div>
        </div>
      )
    },
    { 
      header: 'Open Rate', 
      key: 'openRate',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className={`font-semibold ${
            value > 30 ? 'text-green-600' : 
            value > 20 ? 'text-yellow-600' : 
            value > 0 ? 'text-red-600' : 'text-gray-400'
          }`}>
            {value > 0 ? `${value}%` : '-'}
          </div>
          {value > 0 && (
            <Eye className="w-3 h-3 text-gray-400" />
          )}
        </div>
      )
    },
    { 
      header: 'Revenue', 
      key: 'revenue',
      render: (value) => (
        <span className={`font-bold ${
          value > 0 ? 'text-primary-600' : 'text-gray-400'
        }`}>
          {value > 0 ? `${value.toLocaleString('ro-RO')} RON` : '-'}
        </span>
      )
    },
    { 
      header: 'AI', 
      key: 'aiOptimization',
      render: (value) => (
        <div className="flex items-center space-x-1">
          {value ? (
            <>
              <Zap className="w-3 h-3 text-yellow-500" />
              <span className="text-xs text-green-600">On</span>
            </>
          ) : (
            <span className="text-xs text-gray-500">Off</span>
          )}
        </div>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => {
              setSelectedCampaign(row);
              setShowCampaignModal(true);
            }}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Vezi detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Campanii Email Personalizate</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Email marketing automat cu personalizare bazată pe comportamentul clientului și AI optimization
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            className="btn-secondary"
            onClick={() => setShowTemplateModal(true)}
          >
            <Mail className="w-4 h-4 mr-2" />
            Template-uri
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Raport
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Campanie Nouă
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card title="Total Campanii" subtitle="Toate timpurile" icon={Mail}>
          <div className="text-2xl font-bold text-primary-600">{emailData.overview.totalCampaigns}</div>
          <div className="text-sm text-blue-600">{emailData.overview.activeCampaigns} active acum</div>
        </Card>
        <Card title="Total Subscriberi" subtitle="Lista activă" icon={Users}>
          <div className="text-2xl font-bold text-primary-600">{emailData.overview.totalSubscribers.toLocaleString('ro-RO')}</div>
          <div className="text-sm text-green-600">{emailData.overview.monthlyGrowth} luna aceasta</div>
        </Card>
        <Card title="Open Rate Mediu" subtitle="Ultimele 30 zile" icon={Eye}>
          <div className="text-2xl font-bold text-primary-600">{emailData.performance.avgOpenRate}%</div>
          <div className="text-sm text-green-600">+2.3% față de luna trecută</div>
        </Card>
        <Card title="Click Rate Mediu" subtitle="Ultimele 30 zile" icon={MousePointer}>
          <div className="text-2xl font-bold text-primary-600">{emailData.performance.avgClickRate}%</div>
          <div className="text-sm text-green-600">+0.8% față de luna trecută</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email Campaigns Table */}
        <div className="lg:col-span-2">
          <Card title="Campanii Email" subtitle="Overview și performanță campanii">
            <Table columns={campaignColumns} data={emailData.campaigns} />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="AI Insights" icon={Zap}>
            <div className="space-y-4">
              {emailData.aiInsights.map((insight) => (
                <div key={insight.id} className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-l-4 border-blue-500 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="error" className="text-xs">
                      {insight.impact}
                    </Badge>
                    <div className="flex items-center space-x-1">
                      <Zap className="w-3 h-3 text-blue-500" />
                      <span className="text-xs text-blue-600">{insight.confidence}%</span>
                    </div>
                  </div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                    {insight.insight}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    <strong>Recomandare:</strong> {insight.recommendation}
                  </div>
                  <Badge variant="default" className="text-xs">
                    {insight.category}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Template-uri Populare" icon={Mail}>
            <div className="space-y-3">
              {emailData.templates.map((template) => (
                <div key={template.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-sm text-gray-900 dark:text-white">
                      {template.name}
                    </div>
                    <span className="text-xs text-gray-500">{template.usage} utilizări</span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {template.description}
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="info" className="text-xs">{template.category}</Badge>
                    {template.aiOptimized && (
                      <Zap className="w-3 h-3 text-yellow-500" title="AI Optimized" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Audience Segments */}
      <Card title="Segmentarea Audience" subtitle="Performanța pe segmente de clienți">
        <Table 
          columns={[
            { header: 'Segment', key: 'name' },
            { header: 'Dimensiune', key: 'size', render: (value) => value.toLocaleString('ro-RO') },
            { header: 'Open Rate Mediu', key: 'avgOpenRate', render: (value) => `${value}%` },
            { header: 'Click Rate Mediu', key: 'avgClickRate', render: (value) => `${value}%` },
            { header: 'Revenue/User', key: 'avgRevenue', render: (value) => `${value} RON` },
            { header: 'Creștere', key: 'growthRate' }
          ]}
          data={emailData.segments} 
        />
      </Card>

      {/* Create Campaign Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Creează Campanie Email Nouă"
        size="large"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Numele Campaniei
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="Ex: Newsletter Aprilie 2024"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipul Campaniei
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                <option>Newsletter</option>
                <option>Promoțională</option>
                <option>Automată</option>
                <option>Survey</option>
                <option>Re-engagement</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject Line
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="Ex: 🚀 Noutățile lunii + oferte exclusive doar pentru tine"
            />
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Zap className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">AI Enhancement Preview</span>
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              Campania va fi optimizată automat pentru timing, subject line, și conținut bazat pe comportamentul
              segmentului selectat. Estimăm o îmbunătățire de 15-25% a open rate-ului și 8-15% a click rate-ului.
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Anulează
            </button>
            <button className="btn-secondary">
              Previzualizează
            </button>
            <button className="btn-primary">
              <Send className="w-4 h-4 mr-2" />
              Creează Campania
            </button>
          </div>
        </div>
      </Modal>

      {/* Templates Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title="Template-uri Email"
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Template-uri optimizate pentru diferite tipuri de campanii
            </p>
            <button className="btn-primary btn-sm">
              <Plus className="w-3 h-3 mr-1" />
              Template Nou
            </button>
          </div>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {emailData.templates.map((template) => (
              <div key={template.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {template.name}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <Badge variant="default" className="text-xs">
                      {template.category}
                    </Badge>
                    {template.aiOptimized && (
                      <Zap className="w-3 h-3 text-yellow-500" title="AI Optimized" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {template.description}
                </p>
                <div className="flex space-x-2 mt-3">
                  <button className="btn-primary btn-sm">
                    Folosește
                  </button>
                  <button className="btn-secondary btn-sm">
                    Previzualizează
                  </button>
                  <button className="btn-secondary btn-sm">
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Campaign Details Modal */}
      <Modal
        isOpen={showCampaignModal}
        onClose={() => {
          setShowCampaignModal(false);
          setSelectedCampaign(null);
        }}
        title={`Detalii Campanie: ${selectedCampaign?.name || ''}`}
        size="large"
      >
        {selectedCampaign && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Tip:</span>
                    <Badge variant={getTypeColor(selectedCampaign.type)}>{selectedCampaign.type}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Status:</span>
                    <Badge variant={getStatusColor(selectedCampaign.status)}>{selectedCampaign.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Template:</span>
                    <span>{selectedCampaign.template}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Audience & Performance</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Segment:</span>
                    <span className="font-medium">{selectedCampaign.audience}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Destinatari:</span>
                    <span className="font-medium">{selectedCampaign.audienceSize.toLocaleString('ro-RO')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Revenue:</span>
                    <span className="font-bold text-primary-600">{selectedCampaign.revenue.toLocaleString('ro-RO')} RON</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Subject Line</h4>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedCampaign.subject}
                </p>
              </div>
            </div>

            {selectedCampaign.status !== 'Programată' && selectedCampaign.openRate > 0 && (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Rezultate Performanță</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${
                      selectedCampaign.openRate > 30 ? 'text-green-600' : 
                      selectedCampaign.openRate > 20 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {selectedCampaign.openRate}%
                    </div>
                    <div className="text-sm text-gray-500">Open Rate</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${
                      selectedCampaign.clickRate > 5 ? 'text-green-600' : 
                      selectedCampaign.clickRate > 3 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {selectedCampaign.clickRate}%
                    </div>
                    <div className="text-sm text-gray-500">Click Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">
                      {selectedCampaign.conversionRate}%
                    </div>
                    <div className="text-sm text-gray-500">Conversion</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">
                      {selectedCampaign.revenue.toLocaleString('ro-RO')}
                    </div>
                    <div className="text-sm text-gray-500">RON Revenue</div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">
                <Copy className="w-4 h-4 mr-2" />
                Duplică
              </button>
              <button className="btn-secondary">
                <Download className="w-4 h-4 mr-2" />
                Export Raport
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowCampaignModal(false);
                  setSelectedCampaign(null);
                }}
              >
                Închide
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CampaniiEmail;
